import { debugOn } from '@/lib/debug';
import { Overlay } from '@/components/Overlay';
import type { AnalyticsDto, PostDto } from '@str/api/types';
import { CHANNELS, type Channel } from '@str/shared';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { postName } from '@/lib/post-title';
import { jobSummary, publishFailure } from '@/lib/publish-status';
import { useToast } from '@/components/toast';
import { ChannelDots, EmptyState, ErrorText, Pill } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { bookingValueError, parseBookingCents } from '@/lib/booking';
import { compact, shortDate, toIsoDate } from '@/lib/format';
import { useActiveProperty } from '@/lib/property-store';
import { scheduledLabel } from '@/lib/schedule';
import {
  bookingsApi,
  devApi,
  isPublishing,
  keys,
  useAnalytics,
  useArchivedPosts,
  useArchivePost,
  useMe,
  usePosts,
  useRestorePost,
} from '@/lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import { mediaStyle } from '@/lib/scenes';

type Tab = 'Published' | 'Scheduled' | 'Drafts' | 'Archived';
const TABS: Tab[] = ['Published', 'Scheduled', 'Drafts', 'Archived'];

function stats(p: PostDto): string {
  if (p.status === 'failed') return `Failed · ${publishFailure(p.publishError, p.channels)}`;
  if (p.status === 'scheduled') {
    if (isPublishing(p)) return `Publishing · ${jobSummary(p) ?? ''}`;
    return `Scheduled · ${p.channels.join(', ') || 'no channels'}`;
  }
  if (p.status === 'published' && !p.imported && p.publishJobs.length)
    return `Published · ${jobSummary(p)}`;
  if (p.status === 'draft')
    return p.currentVersionId
      ? `Draft · ${p.versions.length || 1} version${p.versions.length > 1 ? 's' : ''}`
      : 'Draft · not generated yet';
  if (p.imported) return 'Example · based on your listing';
  const parts = [`${p.clicks} to site`];
  if (p.bookings) parts.push(`${p.bookings} booking${p.bookings > 1 ? 's' : ''}`);
  return parts.join(' · ');
}

/** "airbnb.com/rooms/6552…" rather than a whole tracking URL. */
function destinationLabel(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname;
    const short = path.length > 18 ? `${path.slice(0, 18)}…` : path;
    return `${u.host.replace(/^www\./, '')}${short}`;
  } catch {
    return url;
  }
}

function dollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

type Booking = AnalyticsDto['bookings'][number];
type BookingInput = {
  valueCents: number;
  bookedOn: string;
  postId: string | null;
  channel: Channel | null;
};

function LogBookingDialog({
  posts,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  posts: PostDto[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: BookingInput) => void;
}) {
  const [value, setValue] = useState('');
  const [bookedOn, setBookedOn] = useState(() => toIsoDate(new Date()));
  const [postId, setPostId] = useState('');
  const [channel, setChannel] = useState('');
  const parsed = parseBookingCents(value);
  const valueError = bookingValueError(parsed);
  const valid = parsed.ok && /^\d{4}-\d{2}-\d{2}$/.test(bookedOn);
  return (
    <Overlay onClose={onClose}>
      <form
        role="dialog"
        aria-labelledby="log-booking-title"
        className="a-rise"
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          if (!valid || busy || !parsed.ok) return;
          onSubmit({
            valueCents: parsed.cents,
            bookedOn,
            postId: postId || null,
            channel: (channel || null) as Channel | null,
          });
        }}
        style={{
          width: 420,
          maxWidth: 'calc(100% - 48px)',
          background: '#fff',
          borderRadius: 20,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: '0 40px 80px -30px rgba(0,0,0,.5)',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span
            id="log-booking-title"
            style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.025em' }}
          >
            Log a booking from social
          </span>
          <span style={{ fontSize: 13.5, color: '#777', lineHeight: 1.5 }}>
            A guest mentioned a post, a DM or your bio link? Record it here so the tiles show what
            social actually brings in.
          </span>
        </div>
        <div className="field">
          <label htmlFor="booking-value">Value (USD)</label>
          <input
            id="booking-value"
            inputMode="decimal"
            placeholder="840"
            value={value}
            autoFocus
            aria-invalid={valueError ? true : undefined}
            aria-describedby={valueError ? 'booking-value-error' : undefined}
            onChange={(e) => setValue(e.target.value)}
          />
          {valueError && (
            <div id="booking-value-error" className="err" role="alert">
              {valueError}
            </div>
          )}
        </div>
        <div className="field">
          <label htmlFor="booking-date">Booked on</label>
          <input
            id="booking-date"
            type="date"
            value={bookedOn}
            onChange={(e) => setBookedOn(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="booking-post">Post it came from</label>
          <select id="booking-post" value={postId} onChange={(e) => setPostId(e.target.value)}>
            <option value="">Not tied to one post</option>
            {posts.map((p) => (
              <option key={p.id} value={p.id}>
                {postName(p)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="booking-channel">Channel</label>
          <select id="booking-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
            <option value="">Not sure</option>
            {CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {error && <div className="err">{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn btn-ghost hv" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary hv" disabled={!valid || busy}>
            {busy ? 'Logging…' : 'Log booking'}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

export function Posts() {
  const { active } = useActiveProperty();
  const posts = usePosts(active.id);
  const analytics = useAnalytics(active.id);
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<Tab>((location.state as { tab?: Tab } | null)?.tab ?? 'Published');
  // Archived posts live on their own tab only: never in the others, the failed count or the tiles.
  const all = (posts.data ?? []).filter((p) => !p.archivedAt);
  const archived = useArchivedPosts(active.id).data ?? [];
  const archivePost = useArchivePost(active.id);
  const restorePost = useRestorePost(active.id);
  const published = all.filter((p) => p.status === 'published').sort((a, b) => b.clicks - a.clicks);
  // A post that did not go out needs the host first: it leads the list, and the tab says so.
  const failed = all.filter((p) => p.status === 'failed');
  const scheduled = [
    ...failed,
    ...all
      .filter((p) => p.status === 'scheduled')
      .sort((a, b) => (a.scheduledAt ?? '').localeCompare(b.scheduledAt ?? '')),
  ];
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const archiveOrRestore = (p: PostDto) =>
    (tab === 'Archived' ? restorePost : archivePost).mutate(p.id, {
      onSuccess: () =>
        toast(tab === 'Archived' ? 'Restored' : 'Archived · kept on the Archived tab'),
      onError: (e) => toast(errorMessage(e), { kind: 'error' }),
    });
  const [running, setRunning] = useState(false);
  const runPublisher = async () => {
    setRunning(true);
    try {
      const r = await devApi.runPublisher();
      await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
      toast(`Publisher ran · ${r.published} published · ${r.pending} pending · ${r.failed} failed`);
    } catch (e) {
      toast(errorMessage(e), { kind: 'error' });
    } finally {
      setRunning(false);
    }
  };
  const [refreshing, setRefreshing] = useState(false);
  const refreshPlans = async () => {
    setRefreshing(true);
    try {
      const r = await devApi.refreshPlans();
      await qc.invalidateQueries({ queryKey: keys.property(active.id) });
      toast(
        `Plans refreshed · ${r.refreshed.length} ${r.refreshed.length === 1 ? 'property' : 'properties'}`,
      );
    } catch (e) {
      toast(errorMessage(e), { kind: 'error' });
    } finally {
      setRefreshing(false);
    }
  };
  const [logOpen, setLogOpen] = useState(false);
  const [logBusy, setLogBusy] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const refreshNumbers = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: keys.analytics(active.id) }),
      qc.invalidateQueries({ queryKey: keys.posts(active.id) }),
    ]);
  const logBooking = async (input: BookingInput) => {
    setLogBusy(true);
    setLogError(null);
    try {
      await bookingsApi.log(active.id, input);
      await refreshNumbers();
      setLogOpen(false);
      toast('Booking logged');
    } catch (e) {
      setLogError(errorMessage(e));
    } finally {
      setLogBusy(false);
    }
  };
  const removeBooking = async (b: Booking) => {
    try {
      await bookingsApi.remove(b.id);
      await refreshNumbers();
      toast('Booking removed');
    } catch (e) {
      toast(errorMessage(e), { kind: 'error' });
    }
  };
  const copyLink = async () => {
    const url = analytics.data?.link.shortUrl;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(`https://${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast('Could not copy · select the link and copy it', { kind: 'error' });
    }
  };
  const drafts = all.filter((p) => p.status === 'draft');
  const attributable = published.filter((p) => !p.imported);
  const loggedBookings = analytics.data?.bookings ?? [];
  const link = analytics.data?.link;
  const shown =
    tab === 'Published'
      ? published
      : tab === 'Scheduled'
        ? scheduled
        : tab === 'Drafts'
          ? drafts
          : archived;
  const counts: Record<Tab, number> = {
    Published: published.length,
    Scheduled: scheduled.length,
    Drafts: drafts.length,
    Archived: archived.length,
  };
  const top = analytics.data?.topPostId;

  return (
    <div
      className="a-rise posts-page"
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'auto' }}
    >
      <div className="posts-head">
        <div className="posts-head-left">
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-.03em' }}>Posts</div>
          <div className="pilltabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                className={`pilltab hv${tab === t ? ' on' : ''}`}
                onClick={() => setTab(t)}
              >
                {t} · {counts[t]}
                {t === 'Scheduled' && failed.length > 0 && (
                  <span className="pilltab-flag">{failed.length} failed</span>
                )}
              </button>
            ))}
          </div>
        </div>
        <span className="posts-actions">
          <button
            type="button"
            className="btn btn-ghost hv"
            style={{ fontSize: 12, padding: '6px 10px' }}
            onClick={() => setLogOpen(true)}
          >
            + Log a booking
          </button>
          {me.data?.features.devMailbox && debugOn() && (
            <button
              type="button"
              className="btn btn-ghost hv"
              style={{ fontSize: 12, padding: '6px 10px' }}
              disabled={running}
              onClick={() => void runPublisher()}
              title="Development only: runs the scheduler tick that the cron runs every 5 minutes"
            >
              {running ? 'Running…' : 'Dev: run publisher'}
            </button>
          )}
          {me.data?.features.devMailbox && debugOn() && (
            <button
              type="button"
              className="btn btn-ghost hv"
              style={{ fontSize: 12, padding: '6px 10px' }}
              disabled={refreshing}
              onClick={() => void refreshPlans()}
              title="Development only: the weekly ideas, starters and best-time refresh the cron runs"
            >
              {refreshing ? 'Refreshing…' : 'Dev: refresh plans'}
            </button>
          )}
          <span className="posts-sort-note">Last 30 days · sorted by clicks to site</span>
        </span>
      </div>
      <div className="posts-kpis">
        {(analytics.data?.kpis ?? []).map((k) => (
          <div
            key={k.key}
            className="card"
            style={{
              borderRadius: 14,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <span style={{ fontSize: 11.5, color: '#888', fontWeight: 600 }}>{k.label}</span>
            <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.02em' }}>
              {typeof k.value === 'number' ? compact(k.value) : k.value}{' '}
              <span
                style={{
                  fontSize: 12,
                  color:
                    k.tone === 'up' ? 'var(--success)' : k.tone === 'down' ? '#b3261e' : '#777',
                  fontWeight: 700,
                }}
              >
                {k.delta ?? ''}
              </span>
            </span>
          </div>
        ))}
        {link && (
          <div
            className="card"
            style={{
              borderRadius: 14,
              padding: '12px 14px',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              minWidth: 0,
            }}
          >
            <span style={{ fontSize: 11.5, color: '#888', fontWeight: 600 }}>
              Your link for the bio and DMs · {link.clicks} click{link.clicks === 1 ? '' : 's'}
            </span>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {link.shortUrl}
              </span>
              <button
                type="button"
                className="btn btn-ghost hv"
                style={{ fontSize: 11.5, padding: '3px 8px', flex: 'none' }}
                onClick={() => void copyLink()}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </span>
            <span style={{ fontSize: 11, color: '#999', lineHeight: 1.4 }}>
              Sends guests to {destinationLabel(link.destination)}, tagged so your booking site can
              show the visits
            </span>
          </div>
        )}
      </div>
      {loggedBookings.length > 0 && (
        <div
          className="card"
          style={{
            borderRadius: 14,
            padding: '10px 14px',
            marginBottom: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 11.5, color: '#888', fontWeight: 600 }}>
            Bookings you logged
          </span>
          {loggedBookings.map((b) => (
            <div
              key={b.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                fontSize: 12.5,
                minWidth: 0,
              }}
            >
              <span style={{ fontWeight: 700, flex: 'none' }}>{dollars(b.valueCents)}</span>
              <span style={{ color: '#777', flex: 'none' }}>{shortDate(b.bookedOn)}</span>
              <span
                style={{
                  color: '#777',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {b.postTitle ?? (b.channel ? `via ${b.channel}` : 'not tied to a post')}
              </span>
              <button
                type="button"
                className="btn btn-ghost hv"
                aria-label="Remove booking"
                title="Remove booking"
                style={{ fontSize: 11.5, padding: '2px 8px', flex: 'none' }}
                onClick={() => void removeBooking(b)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      {tab === 'Archived' && (
        <p className="posts-archived-note">
          Archived posts are kept, never deleted. Restore one any time.
        </p>
      )}
      {posts.isError && !posts.data && tab !== 'Archived' ? (
        <div
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <ErrorText>Couldn&apos;t load your posts. {errorMessage(posts.error)}</ErrorText>
          <button type="button" className="btn-pill hv" onClick={() => void posts.refetch()}>
            Try again
          </button>
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          title={
            tab === 'Drafts'
              ? 'No drafts'
              : tab === 'Scheduled'
                ? 'Nothing scheduled yet'
                : tab === 'Archived'
                  ? 'Nothing archived'
                  : 'No published posts yet'
          }
          body={
            tab === 'Published'
              ? 'Once a scheduled post goes out, its clicks and the bookings you log show up here.'
              : tab === 'Archived'
                ? 'Archive a post from any tab to take it out of your lists.'
                : 'Create a post and add it to the calendar.'
          }
          action={
            <button className="btn btn-primary hv" onClick={() => void navigate('/app/create')}>
              + Create
            </button>
          }
        />
      ) : (
        <div className="posts-grid">
          {shown.map((p, i) => {
            const openable = tab !== 'Archived' && !p.imported && p.status !== 'published';
            return (
              <div
                key={p.id}
                role={openable ? 'button' : undefined}
                tabIndex={openable ? 0 : undefined}
                onClick={() => openable && void navigate(`/app/create?post=${p.id}`)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && openable && void navigate(`/app/create?post=${p.id}`)
                }
                className="hv lift"
                style={{
                  background: '#fff',
                  border:
                    p.id === top && tab === 'Published'
                      ? '1.5px solid #111'
                      : p.status === 'failed'
                        ? '1px solid rgba(163,58,42,.5)'
                        : p.status === 'scheduled' && !p.imported
                          ? '1px dashed rgba(0,0,0,.25)'
                          : '1px solid rgba(0,0,0,.07)',
                  borderRadius: 14,
                  padding: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  minWidth: 0,
                  animation: 'rise .5s both',
                  animationDelay: `${(0.05 + i * 0.05).toFixed(2)}s`,
                  cursor: openable ? 'pointer' : 'default',
                }}
              >
                <div
                  style={{
                    aspectRatio: '4/3',
                    borderRadius: 9,
                    ...mediaStyle({ url: p.imageUrl, scene: p.scene }),
                    boxShadow: 'inset 0 -40px 50px rgba(0,0,0,.22)',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: 7,
                  }}
                >
                  <Pill>{p.format}</Pill>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {p.status === 'failed' && <Pill style={{ background: '#a33a2a' }}>Failed</Pill>}
                    {isPublishing(p) && <Pill style={{ background: '#5a3a22' }}>Publishing…</Pill>}
                    <ChannelDots channels={p.channels} size={8} ring />
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    padding: '0 4px 2px',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 6,
                      fontSize: 13,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                      }}
                    >
                      {postName(p)}
                    </span>
                    <span style={{ color: '#999', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {p.status === 'scheduled' || p.status === 'failed'
                        ? (scheduledLabel(p) ?? shortDate(p.createdAt))
                        : p.scheduledDate
                          ? shortDate(p.scheduledDate)
                          : shortDate(p.createdAt)}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 11.5,
                      color: '#777',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {stats(p)}
                  </div>
                  {p.status === 'published' && !p.imported && (
                    <Link
                      to={`/app/posts/${p.id}/results`}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: 12, fontWeight: 600 }}
                    >
                      See it per channel
                    </Link>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost hv post-row-action"
                    aria-label={`${tab === 'Archived' ? 'Restore' : 'Archive'} ${postName(p)}`}
                    disabled={archivePost.isPending || restorePost.isPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      archiveOrRestore(p);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {tab === 'Archived' ? 'Restore' : 'Archive'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {logOpen && (
        <LogBookingDialog
          posts={attributable}
          busy={logBusy}
          error={logError}
          onClose={() => setLogOpen(false)}
          onSubmit={(input) => void logBooking(input)}
        />
      )}
    </div>
  );
}
